import { action } from '@ember/object';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { task } from 'ember-concurrency';
import Component from '@glimmer/component';
import LinkButton from 'ember-simplepractice/components/spds/action/button/link';
import styles from './conversation-header.module.scss';

export default class ConversationHeader extends Component {
  @service alertManager;
  @service clientProfileNavigator;
  @service intl;
  @service messaging;
  @service messagingNavigation;

  get conversation() {
    return this.args.conversation;
  }

  get filteredUsers() {
    return this.args.filteredUsers;
  }

  get isGroup() {
    return this.conversation?.isGroup ?? false;
  }

  get memberCount() {
    const users = this.conversation?.users ?? [];
    return users.length;
  }

  get participantNames() {
    return this.filteredUsers.map(user => user.name).join(' & ');
  }

  get shouldShowClientProfileLink() {
    return this.clientProfileNavigator.canNavigateToClientProfile(
      this.filteredUsers,
      this.messaging.currentProfile.isClinician
    );
  }

  navigateToClientProfileTask = task({ drop: true }, async () => {
    try {
      this.clientProfileNavigator.navigateToClientProfile(this.conversation, this.filteredUsers);
    } catch (error) {
      this.alertManager.broadcast('toast', {
        message: this.intl.t('emc.general_error'),
        type: 'error',
      });
    }
  });

  @action
  handleClientProfileClick() {
    document.activeElement.blur();
    this.navigateToClientProfileTask.perform();
  }

  @action
  handleGroupHeaderClick() {
    this.messagingNavigation.toGroupConversationInformation();
  }

  <template>
    <div class={{styles.component}}>
      <span class="participant-names-container">
        {{#if this.isGroup}}
          <button
            type="button"
            class="group-message-header"
            aria-label={{t "emc.group_conversation_information.view_aria_label"}}
            data-test-group-message-header
            {{on "click" this.handleGroupHeaderClick}}
          >
            <span class="participant-names" data-test-group-message-title>
              {{t "emc.group_message"}}
            </span>
            <span class="member-count" data-test-group-message-member-count>
              {{t "emc.group_message_member_count" count=this.memberCount}}
            </span>
          </button>
        {{else}}
          {{#if this.shouldShowClientProfileLink}}
            <LinkButton
              {{on "click" this.handleClientProfileClick}}
              class="participant-names"
              data-test-client-profile-link
            >
              {{this.participantNames}}
            </LinkButton>
          {{else}}
            <span class="participant-names">
              {{this.participantNames}}
            </span>
          {{/if}}
        {{/if}}

        {{#if @showAwayIndicator}}
          <span class="away-indicator" data-test-away-indicator>
            {{t "emc.away_status.indicator"}}
          </span>
        {{/if}}
      </span>
    </div>
  </template>
}
